/* The remediation plan queue and the four transitions a human can make.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere. Hard rule 1: the edge sets it from a validated token
 * claim, and a frontend that sends it is asking the backend to trust the browser.
 *
 * **There is no execute mutation and no delete.** Neither exists on the server either. A plan is the
 * record that somebody proposed changing a customer's data; `abandon` closes it and leaves the
 * record. Offering either would be offering something the API refuses.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isArtifactSummary,
  isPlan,
  isScriptPreview,
} from '@/shared/api/generated/remediation';
import type {
  ArtifactFormat,
  ArtifactSummary,
  Plan,
  ScriptPreview,
} from '@/shared/api/generated/remediation';
import { getOne, getPage, postJson } from '@/shared/api/http';

import { orderForQueue, simulationPollDelay } from '../model/plans';

export const PLANS_URL = '/v1/remediation/plans';

export type PlanAction =
  'simulate' | 'submit' | 'approve' | 'reject' | 'emit' | 'abandon';

function planUrl(planId: string, suffix = ''): string {
  return `${PLANS_URL}/${encodeURIComponent(planId)}${suffix}`;
}

export function artifactUrl(planId: string): string {
  return planUrl(planId, '/artifact');
}

const QUERY_KEY = ['remediation', 'plans'] as const;

/* RFC 9457 Problem Details. Every backend error serialises to this, so the UI shows the server's own
 * explanation — "a remediation plan cannot go from drafted to awaiting_approval" is far more useful
 * than "Request failed", and on this screen the refusals *are* the product. */

async function fetchPlans(): Promise<readonly Plan[]> {
  /* `getPage` validates per item with the generated guard, which is what this needs: a malformed
   * proposal reaching the tier lookup would render an unstyled badge — a Tier D finding displayed as
   * nothing in particular, on the screen whose entire job is distinguishing "we can fix this" from
   * "we cannot". It also attaches the bearer token, which the local transport this replaced did not,
   * so the queue 401'd under ADR-012's local auth. */
  const page = await getPage(PLANS_URL, isPlan, 'remediation plan');
  return orderForQueue([...page.items]);
}

export function usePlanQueue(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<readonly Plan[]> {
  /* `enabled` so a caller that has already decided the reader may not see plans does not issue the
     read and take a 403 back — the notification bell's four-way access answer, which turns an
     ordinary permission boundary into a reported nothing rather than a fault. Defaulted true, so
     the queue screen's own call is unchanged. */
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchPlans,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

const PLAN_KEY = (id: string) => ['remediation', 'plan', id] as const;

/** One plan, for its own screen.
 *
 * A separate read rather than picking the row out of the queue's cache: the detail page is
 * reachable by URL, so it has to work with no queue ever having been fetched — and a plan whose
 * state moved since the queue was read must not be acted on from a stale row.
 */
export function usePlan(planId: string): UseQueryResult<Plan> {
  return useQuery({
    queryKey: PLAN_KEY(planId),
    enabled: planId !== '',
    queryFn: () => getOne(planUrl(planId), isPlan, 'remediation plan'),
    retry: false,
    /* A 202'd measurement needs a next fetch, and the invalidation on success is too early to be
       it. The predicate lives in the model, where it is tested — the wrong condition here polls
       for ever on every plan a steward opens. */
    refetchInterval: (query) =>
      simulationPollDelay(query.state.data, query.state.dataUpdateCount),
  });
}

const ARTIFACTS_KEY = (id: string) => ['remediation', 'artifacts', id] as const;

/** What has been emitted, as metadata. **The script itself is not in this response** — it comes
 * down the download route alone, which is what keeps "who fetched the script" one answerable
 * question rather than something every page render did invisibly. */
export function usePlanArtifacts(
  planId: string,
  enabled: boolean,
): UseQueryResult<readonly ArtifactSummary[]> {
  return useQuery({
    queryKey: ARTIFACTS_KEY(planId),
    enabled: enabled && planId !== '',
    queryFn: async () =>
      (await getPage(planUrl(planId, '/artifacts'), isArtifactSummary, 'artifact')).items,
    retry: false,
  });
}

const PREVIEW_KEY = (id: string) =>
  ['remediation', 'plan', id, 'script-preview'] as const;

/** The script this plan would emit, read on demand and never on mount.
 *
 * **`enabled` is the whole design of this hook.** The server renders the same bytes the emit route
 * would, and the sibling `/artifacts` route withholds script content precisely so that handing over
 * a script stays something somebody asked for rather than something every page render did
 * invisibly. Fetching this on mount would be that, one route over. The caller passes `false` until
 * a reader presses the control, exactly as the execution assessment is gated -- and for the same
 * stated reason, which that screen puts in words on the page.
 *
 * Cached under the plan's key, so re-rendering does not re-fetch and the section stays open while a
 * reader scrolls it. **Invalidated by every plan action** (see `usePlanAction`), because an
 * assessment changes the row counts the script cites: a preview surviving a re-assessment would
 * quote numbers the plan no longer holds, which is the one way this section could lie.
 */
export function useScriptPreview(
  planId: string,
  enabled: boolean,
): UseQueryResult<ScriptPreview> {
  return useQuery({
    queryKey: PREVIEW_KEY(planId),
    enabled: enabled && planId !== '',
    queryFn: () =>
      getOne(planUrl(planId, '/script-preview'), isScriptPreview, 'script preview'),
    retry: false,
  });
}

/* One key per plan *and per action*, minted when the row renders rather than on click.
 *
 * Minting inside the handler would produce a fresh key per attempt, so a double-click through a slow
 * network sends two requests the server treats as unrelated — and on `/simulate` that means two
 * connections opened against a customer's production database. Keying per (plan, action) means a
 * retry of the same decision carries the same key, and approving then emitting are two decisions. */
const keys = new Map<string, string>();

export function idempotencyKeyFor(planId: string, action: PlanAction): string {
  const slot = `${action}:${planId}`;
  const existing = keys.get(slot);
  if (existing !== undefined) return existing;
  const minted = `${action}-${planId}-${crypto.randomUUID()}`;
  keys.set(slot, minted);
  return minted;
}

interface ActionRequest {
  readonly planId: string;
  readonly action: PlanAction;
  readonly reason?: string;
  readonly connectionId?: string;
  readonly format?: ArtifactFormat;
}

function bodyFor(request: ActionRequest): object | undefined {
  switch (request.action) {
    case 'simulate':
      return { connection_id: request.connectionId ?? '' };
    case 'approve':
      return { approved: true, reason: request.reason ?? '' };
    case 'reject':
      return { approved: false, reason: request.reason ?? '' };
    case 'emit':
      return { format: request.format ?? 'sql_script' };
    default:
      return undefined;
  }
}

const PATHS: Readonly<Record<PlanAction, string>> = {
  simulate: '/simulate',
  submit: '/submit',
  approve: '/decisions',
  reject: '/decisions',
  emit: '/emit',
  abandon: '/abandon',
};

async function act(request: ActionRequest): Promise<Plan> {
  const body = bodyFor(request);
  /* `postJson`, not `post`: `/emit` answers `{plan, artifact}` and every other action answers the
   * plan itself, so the generated guard cannot be applied to the raw body — it is applied below to
   * whichever half carries the plan. The key and the token come from the shared transport. */
  const payload = await postJson(planUrl(request.planId, PATHS[request.action]), {
    idempotencyKey: idempotencyKeyFor(request.planId, request.action),
    ...(body === undefined ? {} : { body }),
  });
  /* Unwrapped here so the mutation's callers see one shape — the artifact is downloaded from its own
   * route, which is also the route that carries the checksum header. */
  const candidate =
    typeof payload === 'object' && payload !== null && 'plan' in payload
      ? payload.plan
      : payload;
  if (!isPlan(candidate)) {
    throw new Error('action response did not match the Plan schema');
  }
  return candidate;
}

export function usePlanAction(): UseMutationResult<Plan, Error, ActionRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: act,
    /* Refetch rather than patching the cache with the response. `/simulate` returns 202 with the
     * plan *unchanged* — the measurement happens in a worker — so writing the response into the
     * cache would be correct and useless, and a user watching for row counts needs the next fetch.
     * A single approval on a two-eyes plan is the same shape: the response is honestly unchanged. */
    onSettled: (_plan, _failure, request) => {
      void client.invalidateQueries({ queryKey: QUERY_KEY });
      /* The open detail page reads the same plan through its own key, and an emit also changes
         what the artifacts listing holds. Refreshing one and not the others would leave a
         screen asserting a state the server has moved past. */
      void client.invalidateQueries({ queryKey: PLAN_KEY(request.planId) });
      void client.invalidateQueries({ queryKey: ARTIFACTS_KEY(request.planId) });
      /* And the rendered script, because an assessment changes the row counts its comments cite
         and an approval changes its header from "NOT YET APPROVED" to two named approvers. A
         preview left in the cache across either would be a review of a plan that has moved. */
      void client.invalidateQueries({ queryKey: PREVIEW_KEY(request.planId) });
    },
  });
}
